import { EventInput } from '@fullcalendar/core';
import { useExternalCalendarFullCalendarEvents } from 'features/external-connections';
import { useMemo } from 'react';
import { useFilterSlices } from 'shared/contexts/filter-slices';
import { useUpcomingCalendarViewOptions } from 'shared/contexts/upcoming-calendar-view-options';
import { ExternalCalendarEventExtendedProps } from 'shared/lib/full-calendar';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { filterCalendarTasksByViewOptions } from 'shared/utils/filter-calendar-tasks-by-view-options';
import { getHiddenLifeAreaGoalIds } from 'shared/utils/get-hidden-life-area-goal-ids';

import { useCalendarTasks } from './use-calendar-tasks';
import { useGoalDeadlineEvents } from './use-goal-deadline-events';
import { useTaskEvents } from './use-task-events';

export type UpcomingCalendarEvents = {
  tasks: Task[];
  events: EventInput[];
};

export type Options = {
  startDate: Timestamp;
  dayRange: number;
  bufferWeeks?: number;
  // Month renders a week-aligned 6-week grid, so its external window is wider than its task window.
  externalStartDate?: Timestamp;
  externalDayRange?: number;
};

export const useUpcomingCalendarEvents = ({
  startDate,
  dayRange,
  bufferWeeks,
  externalStartDate,
  externalDayRange,
}: Options): UpcomingCalendarEvents => {
  const { viewOptions } = useUpcomingCalendarViewOptions();
  const slices = useFilterSlices();

  const tasks = useCalendarTasks(startDate, dayRange, bufferWeeks);

  const hiddenGoalIds = useMemo(
    () =>
      getHiddenLifeAreaGoalIds(
        viewOptions.hiddenLifeAreaIds,
        slices?.lifeAreaGoalIdsMap,
      ),
    [viewOptions.hiddenLifeAreaIds, slices?.lifeAreaGoalIdsMap],
  );

  const filteredTasks = useMemo(
    () =>
      filterCalendarTasksByViewOptions(
        tasks.filter(({ deletedAt }) => !deletedAt),
        viewOptions,
        {
          hiddenGoalIds,
          lifeAreaLinkedGoalIds: slices?.lifeAreaLinkedGoalIds,
        },
      ),
    [tasks, viewOptions, hiddenGoalIds, slices?.lifeAreaLinkedGoalIds],
  );

  const taskEvents = useTaskEvents(filteredTasks, {
    rangeReferenceDate: startDate,
    dayRange,
    hideCompleted: viewOptions.hideCompleted,
  });
  const goalDeadlineEvents = useGoalDeadlineEvents(hiddenGoalIds);
  const externalCalendarEvents = useExternalCalendarFullCalendarEvents(
    externalStartDate ?? startDate,
    externalDayRange ?? dayRange,
  );

  const visibleExternalEvents = useMemo(() => {
    if (viewOptions.hideExternalCalendarEvents) {
      return [];
    }

    if (!viewOptions.hiddenCalendarIds.length) {
      return externalCalendarEvents;
    }

    return externalCalendarEvents.filter((event) => {
      const { calendarId } = (event.extendedProps ??
        {}) as ExternalCalendarEventExtendedProps;

      return !calendarId || !viewOptions.hiddenCalendarIds.includes(calendarId);
    });
  }, [
    externalCalendarEvents,
    viewOptions.hideExternalCalendarEvents,
    viewOptions.hiddenCalendarIds,
  ]);

  const events = useMemo(
    () => [...visibleExternalEvents, ...taskEvents, ...goalDeadlineEvents],
    [goalDeadlineEvents, taskEvents, visibleExternalEvents],
  );

  return { tasks: filteredTasks, events };
};
